import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import Pagination from "../components/Pagination";
import { ProductService, Product } from "../services/productService";
import { CategoryService, Category } from "../services/categoryService";
import { ProductTypeService, ProductType } from "../services/productTypeService";

const Shop: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [productTypes, setProductTypes] = useState<ProductType[]>([]);

  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedProductType, setSelectedProductType] = useState("");

  const [priceRange, setPriceRange] = useState(0);
  const [maxPrice, setMaxPrice] = useState(20000000);

  const [stockFilter, setStockFilter] = useState(0);
  const [maxStock, setMaxStock] = useState(100);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const itemsPerPage = 12;
  const [searchParams] = useSearchParams();

  const fetchProducts = async (page = 1) => {
    setLoading(true);
    try {
      const q = searchParams.get("q") || undefined;

      const res = await ProductService.getProducts({
        page,
        limit: itemsPerPage,
        categoryId: selectedCategory || undefined,
        productTypeId: selectedProductType || undefined,
        minPrice: 0,
        maxPrice: priceRange > 0 ? priceRange : undefined,
        minStock: stockFilter > 0 ? stockFilter : undefined,
        search: q,
      });

      setProducts(res.data);
      setTotalPages(res.pagination?.totalPages ?? 1);
    } catch (err) {
      console.error("Failed to fetch products:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [cats, types] = await Promise.all([
          CategoryService.getAllCategories(),
          ProductTypeService.getAll(),
        ]);
        setCategories(cats);
        setProductTypes(types);

        const prods = await ProductService.getProducts({ page: 1, limit: 1000 });
        if (prods.data.length > 0) {
          const maxP = Math.max(...prods.data.map((p) => Number(p.price)));
          const maxS = Math.max(
            ...prods.data.map((p) => Number(p.stock_quantity || 0))
          );
          setMaxPrice(maxP);
          setPriceRange(maxP);
          setMaxStock(maxS);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    fetchProducts(currentPage);
  }, [
    currentPage,
    selectedCategory,
    selectedProductType,
    priceRange,
    stockFilter,
    searchParams,
  ]);

  return (
    <div className="min-h-screen bg-gray-100 px-4 py-8">
      <div className="flex flex-col gap-8 md:flex-row">
        {/* Sidebar */}
        <aside className="w-full md:w-1/4 bg-white rounded-xl p-6 shadow-sm sticky top-4 self-start">
          <div className="mb-6">
            <h3 className="uppercase text-lg font-semibold text-gray-800">
              Shop
            </h3>
            <p className="text-sm text-gray-500">
              Find the perfect product
            </p>
          </div>

          <h2 className="uppercase text-base font-semibold text-gray-700 mb-4">
            Filters
          </h2>

          {/* Category */}
          <label className="block text-sm font-medium text-gray-600 mb-1">
            Category
          </label>
          <select
            className="w-full border border-gray-300 rounded-md p-2 mb-4"
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>

          {/* Product Type */}
          {productTypes.length > 0 && (
            <>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Product Type
              </label>
              <select
                className="w-full border uppercase border-gray-300 rounded-md p-2 mb-4"
                value={selectedProductType}
                onChange={(e) => {
                  setSelectedProductType(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Types</option>
                {productTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </>
          )}

          {/* Price */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Max Price: UGX {priceRange.toLocaleString()}
            </label>
            <input
              type="range"
              min={0}
              max={maxPrice}
              step={100}
              value={priceRange}
              onChange={(e) => {
                setPriceRange(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="w-full"
            />
          </div>

          {/* Stock */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Minimum Stock: {stockFilter}{" "}
              {stockFilter === 0 && "(All)"}
            </label>
            <input
              type="range"
              min={0}
              max={maxStock}
              step={1}
              value={stockFilter}
              onChange={(e) => {
                setStockFilter(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="w-full"
            />
          </div>
        </aside>

        {/* Products */}
        <main className="flex-1">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-500">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-black mb-4" />
              <p>Loading...</p>
            </div>
          ) : products.length > 0 ? (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {products.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            </>
          ) : (
            <p className="text-center text-gray-500 mt-10">
              No products found.
            </p>
          )}
        </main>
      </div>
    </div>
  );
};

export default Shop;
